import React from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { colors, glow, layout, radius, spacing, typography } from '../config/theme';

import { Sticker, pickSticker } from './brand';


// Support tints for chips / banners. Keys kept from the old palette so call
// sites don't churn; values are now craft-paper friendly.
export const tint = {
  tealGhost: 'rgba(75,122,82,0.16)', // folk green — "готово"
  tealEdge: 'rgba(75,122,82,0.42)',
  amber: '#C6892F', // ochre
  amberGhost: 'rgba(198,137,47,0.16)',
  rose: '#A32E22', // oxblood — warnings / destructive
  roseGhost: 'rgba(163,46,34,0.14)',
  violet: '#3F5C7A', // folk blue
};

export function SectionLabel({ children, style }) {
  return (
    <Text style={[s.sectionLabel, style]}>
      <Text style={{ color: colors.primary }}>◆  </Text>
      {String(children).toUpperCase()}
    </Text>
  );
}

// `glow` gives the card a teal edge + halo (used for the live shift card).
export function Card({ children, style, onPress, onLongPress, accent, glow: isGlow }) {
  const content = (
    <View style={[s.card, accent && { borderColor: accent }, isGlow && s.cardGlow, style]}>{children}</View>
  );
  if (!onPress && !onLongPress) return content;
  return <Pressable onPress={onPress} onLongPress={onLongPress} delayLongPress={350} style={({ pressed }) => (pressed ? s.pressed : null)}>{content}</Pressable>;
}

export function Chip({ label, kind = 'muted' }) {
  const map = {
    muted: { bg: colors.surfaceElevated, fg: colors.textSecondary, bd: 'transparent' },
    block: { bg: tint.amberGhost, fg: tint.amber, bd: 'rgba(198,137,47,0.32)' },
    good: { bg: tint.tealGhost, fg: colors.success, bd: tint.tealEdge },
    bad: { bg: tint.roseGhost, fg: tint.rose, bd: 'rgba(163,46,34,0.28)' },
  }[kind];
  return (
    <View style={[s.chip, { backgroundColor: map.bg, borderColor: map.bd }]}>
      <Text style={[s.chipText, { color: map.fg }]}>{label}</Text>
    </View>
  );
}

export function Pill({ label, kind = 'muted' }) {
  const map = {
    muted: { bg: 'rgba(34,31,31,0.07)', fg: colors.textSecondary },
    good: { bg: tint.tealGhost, fg: colors.success },
    bad: { bg: tint.roseGhost, fg: tint.rose },
    warn: { bg: tint.amberGhost, fg: tint.amber },
  }[kind];
  return (
    <View style={[s.pill, { backgroundColor: map.bg }]}>
      <Text style={[s.pillText, { color: map.fg }]}>{String(label).toUpperCase()}</Text>
    </View>
  );
}

export function Avatar({ label, kind = 'teal' }) {
  const bg = { teal: colors.primary, violet: tint.violet, orange: colors.warning, muted: colors.surfaceElevated }[kind];
  const fg = kind === 'muted' ? colors.textSecondary : colors.onPrimary;
  return (
    <View style={[s.avatar, { backgroundColor: bg }]}>
      <Text style={[s.avatarText, { color: fg }]}>{label}</Text>
    </View>
  );
}

export function Toggle({ value, onValueChange, disabled }) {
  return (
    <Pressable
      onPress={() => !disabled && onValueChange(!value)}
      style={[s.toggle, value ? s.toggleOn : s.toggleOff, disabled && s.disabled]}
    >
      <View style={[s.knob, value ? s.knobOn : s.knobOff]} />
    </Pressable>
  );
}

export function PrimaryButton({ title, onPress, loading, disabled, kind = 'primary' }) {
  const isPrimary = kind === 'primary';
  const bg = kind === 'danger' ? colors.danger : colors.primary;
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      style={({ pressed }) => [
        s.btn,
        { backgroundColor: bg },
        isPrimary && s.btnGlow,
        pressed && !disabled && s.btnPressed,
        (disabled || loading) && s.disabled,
      ]}
    >
      {loading ? <ActivityIndicator color={colors.onPrimary} /> : <Text style={s.btnText}>{title}</Text>}
    </Pressable>
  );
}

export function GhostButton({ title, onPress, disabled }) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [s.ghostBtn, pressed && s.pressed, disabled && s.disabled]}
    >
      <Text style={s.ghostBtnText}>{title}</Text>
    </Pressable>
  );
}

export function TextButton({ title, onPress, kind = 'primary' }) {
  const color = { primary: colors.primary, danger: colors.danger, muted: colors.textSecondary }[kind];
  return (
    <Pressable onPress={onPress} hitSlop={8} style={({ pressed }) => pressed && s.pressed}>
      <Text style={[s.textBtn, { color }]}>{title}</Text>
    </Pressable>
  );
}

export function Field({ label, value, onChangeText, placeholder, keyboardType, autoCapitalize, multiline, secureTextEntry }) {
  return (
    <View style={s.field}>
      {label ? <Text style={s.fieldLabel}>{String(label).toUpperCase()}</Text> : null}
      <TextInput
        style={[s.input, multiline && s.inputMultiline]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.textMuted}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        multiline={multiline}
        secureTextEntry={secureTextEntry}
      />
    </View>
  );
}

export function ErrorBanner({ message, onRetry }) {
  if (!message) return null;
  return (
    <View style={s.errorBox}>
      <Text style={s.errorText}>{message}</Text>
      {onRetry ? <TextButton title="Ещё раз" onPress={onRetry} /> : null}
    </View>
  );
}

export function Empty({ text, sticker }) {
  return (
    <View style={s.emptyWrap}>
      <Sticker name={sticker || pickSticker(String(text || ''))} size={92} style={s.emptySticker} />
      <Text style={s.empty}>{text}</Text>
    </View>
  );
}

export function Loader() {
  return (
    <View style={s.loader}>
      <ActivityIndicator color={colors.primary} />
    </View>
  );
}

export const screen = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.background },
  // Centred column so wide screens (laptop / tablet) don't get one endless
  // phone-width strip.
  content: {
    paddingHorizontal: spacing.lg,
    // Extra top room so screens without a nav header don't butt against the
    // status bar / notch (the web root is already inset by env(safe-area-*)).
    paddingTop: spacing.xl,
    gap: spacing.md,
    paddingBottom: spacing.xxl,
    width: '100%',
    maxWidth: layout.maxContentWidth,
    alignSelf: 'center',
  },
  contentWide: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl,
    gap: spacing.md,
    paddingBottom: spacing.xxl,
    width: '100%',
    maxWidth: layout.maxWideWidth,
    alignSelf: 'center',
  },
});

const s = StyleSheet.create({
  sectionLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    fontWeight: '800',
    letterSpacing: 1.6,
    marginTop: spacing.md,
    marginBottom: 4,
    marginHorizontal: 2,
  },
  card: {
    backgroundColor: colors.surfaceCard,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.md,
    padding: spacing.md,
  },
  cardGlow: {
    borderColor: colors.primary,
    borderWidth: 1.5,
    ...glow.primary,
    shadowOpacity: 0.18,
  },
  pressed: { opacity: 0.65 },
  chip: { borderWidth: 1, borderRadius: radius.full, paddingHorizontal: 9, paddingVertical: 3 },
  chipText: { fontFamily: typography.caption.fontFamily, fontSize: 10.5, fontWeight: '700' },
  pill: { borderRadius: radius.full, paddingHorizontal: 9, paddingVertical: 3 },
  pillText: { fontFamily: typography.caption.fontFamily, fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  avatar: { width: 34, height: 34, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontFamily: typography.button.fontFamily, fontSize: 12, fontWeight: '800' },
  toggle: { width: 42, height: 24, borderRadius: radius.full, justifyContent: 'center', paddingHorizontal: 3 },
  toggleOn: { backgroundColor: colors.primary, alignItems: 'flex-end' },
  toggleOff: { backgroundColor: colors.surfaceElevated, alignItems: 'flex-start' },
  knob: { width: 18, height: 18, borderRadius: radius.full },
  knobOn: { backgroundColor: colors.onPrimary },
  knobOff: { backgroundColor: colors.surfaceCard },
  disabled: { opacity: 0.4 },
  btn: { borderRadius: radius.md, paddingVertical: spacing.md, alignItems: 'center', justifyContent: 'center' },
  btnGlow: Platform.OS === 'web' ? { boxShadow: '0 10px 24px -10px rgba(138,32,24,0.55)' } : glow.primary,
  btnPressed: { opacity: 0.85, transform: [{ scale: 0.99 }] },
  btnText: { ...typography.button, color: colors.onPrimary },
  ghostBtn: {
    borderRadius: radius.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: 'rgba(237,42,35,0.08)',
  },
  ghostBtnText: { ...typography.button, color: colors.primary },
  textBtn: { ...typography.button, fontSize: 14 },
  field: { gap: 6 },
  fieldLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '800', letterSpacing: 1 },
  input: {
    backgroundColor: colors.inputBackground,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 3,
    color: colors.textPrimary,
    fontFamily: typography.body.fontFamily,
    fontSize: 15,
  },
  inputMultiline: { minHeight: 84, textAlignVertical: 'top' },
  errorBox: {
    backgroundColor: tint.roseGhost,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: 'rgba(255,90,106,0.25)',
    padding: spacing.md,
    gap: spacing.sm,
  },
  errorText: { ...typography.body, color: tint.rose },
  emptyWrap: { alignItems: 'center', justifyContent: 'center', paddingVertical: spacing.xl, gap: spacing.sm },
  emptySticker: { opacity: 0.9 },
  empty: { ...typography.body, color: colors.textMuted, textAlign: 'center' },
  loader: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background },
});
